import type { PillarPage } from "./content-types";

/**
 * The 4 SEO pillar pages. One component renders each at its FR and EN route.
 * Positioning: ReFound = Canadian mobile app turning paper receipts into smart
 * proof of purchase for consumers and families. Careful, non-guaranteed wording.
 */

const FR_LINKS = {
  featureOcr: "/fonctionnalites/precision-ocr",
  featureCloud: "/fonctionnalites/stockage-cloud-securise",
  featureFilters: "/fonctionnalites/categorisation-filtres",
  blogChoose: "/blog/comment-choisir-application-recus",
  receipts: "/application-recus",
  returns: "/application-retours-magasin",
  warranties: "/application-garanties",
  family: "/application-recus-famille",
  blogScan: "/blog/comment-numeriser-recus-application-mobile",
  blogFaded: "/blog/recu-efface-quoi-faire",
  blogWarranty: "/blog/comment-conserver-recus-garanties",
  blogReturn: "/blog/comment-faire-retour-magasin-avec-telephone",
  blogBest: "/blog/meilleure-application-recus-canada",
  blogShare: "/blog/partager-recu-conjoint-famille",
};

const EN_LINKS = {
  featureOcr: "/en/features/ocr-accuracy",
  featureCloud: "/en/features/secure-cloud-storage",
  featureFilters: "/en/features/categories-filters",
  blogChoose: "/en/blog/how-to-choose-receipt-app",
  receipts: "/en/receipt-app",
  returns: "/en/returns-app",
  warranties: "/en/warranty-tracker-app",
  family: "/en/family-receipt-app",
  blogScan: "/en/blog/comment-numeriser-recus-application-mobile",
  blogFaded: "/en/blog/faded-receipt-what-to-do",
  blogWarranty: "/en/blog/how-to-keep-receipts-for-warranties",
  blogReturn: "/en/blog/how-to-return-items-in-store-with-your-phone",
  blogBest: "/en/blog/best-receipt-app-canada",
  blogShare: "/en/blog/share-receipt-with-partner-family",
};

export const pillars: PillarPage[] = [
  /* ============================ 1. RECEIPTS ============================ */
  {
    key: "receipts",
    frPath: FR_LINKS.receipts,
    enPath: EN_LINKS.receipts,
    image: "/og-base.png",
    imageAltFr: "Application de reçus ReFound affichant un reçu enrichi",
    imageAltEn: "ReFound receipt app showing an enriched receipt",
    fr: {
      metaTitle: "Application de reçus intelligents pour retours et garanties | ReFound",
      metaDescription:
        "ReFound est l'application de reçus canadienne qui transforme vos reçus papier en preuves d'achat intelligentes : scannez, enrichissez et retrouvez-les.",
      keywords:
        "application de reçus, meilleure application de reçus, scanner un reçu, numériser un reçu, reçus intelligents, reçus enrichis, retrouver un reçu, preuve d'achat, application de reçus Canada",
      breadcrumb: "Application de reçus",
      eyebrow: "Application de reçus",
      h1: "Une application de reçus qui transforme vos reçus en preuves d'achat intelligentes",
      intro: [
        "ReFound est une application mobile canadienne qui transforme vos reçus papier en preuves d'achat intelligentes. Au lieu de garder seulement une photo, ReFound aide à scanner, enrichir, organiser et retrouver vos reçus pour les retours, les garanties et le partage avec un conjoint ou un membre de la famille.",
        "Pensée pour les consommateurs et les familles, l'application garde vos reçus du quotidien au même endroit, prêts quand vous en avez besoin.",
      ],
      sections: [
        {
          heading: "Plus qu'une photo de reçu : un reçu enrichi",
          body: [
            "La plupart des gens prennent une photo de leur reçu et l'oublient dans leur galerie. Le problème : un reçu papier affiche souvent des codes obscurs, et le papier thermique finit par s'effacer.",
            "ReFound peut enrichir vos reçus avec des informations utiles, quand elles sont disponibles, pour que vous sachiez vraiment ce que vous avez acheté.",
          ],
          bullets: [
            "Nom de produit plus clair",
            "Image du produit",
            "Lien vers la page du produit ou du marchand",
            "Catégorie",
            "Garantie du manufacturier",
            "Date limite de retour estimée",
            "Informations extraites du reçu",
            "Code-barres du reçu quand détecté",
          ],
        },
        {
          heading: "Scanner et numériser un reçu en quelques secondes",
          body: [
            "Prenez une photo de n'importe quel reçu, de n'importe quel marchand. ReFound lit les informations du reçu et garde une copie lisible avant que l'encre du papier thermique ne disparaisse.",
            "Pour un guide complet, voyez [comment numériser des reçus avec une application mobile](/blog/comment-numeriser-recus-application-mobile).",
          ],
        },
        {
          heading: "Retrouver le bon reçu rapidement",
          body: [
            "Quand vient le temps d'un retour ou d'une garantie, le plus difficile est souvent de retrouver le bon reçu. ReFound organise vos reçus pour une recherche rapide par marchand, produit ou date.",
          ],
          bullets: [
            "Recherche rapide du bon reçu",
            "Organisation par marchand et par catégorie",
            "Reçus du foyer au même endroit",
          ],
        },
        {
          heading: "Pensé pour les achats du quotidien au Canada",
          body: [
            "ReFound fonctionne avec les reçus de n'importe quel magasin : chaque reçu est numérisé, organisé et utilisable pour un retour, avec le code-barres quand il y en a un. Pour une liste de marchands populaires comme Canadian Tire, RONA, Home Depot, Walmart, Costco, Best Buy, Sports Experts, Décathlon, BMR, Canac, Bureau en Gros, Dollarama, Tigre Géant, Jean Coutu, Shoppers et Loblaws, les articles sont en plus enrichis avec le vrai nom du produit, une photo, un lien et parfois la garantie lorsque disponible.",
            "Des exemples concrets, tirés de reçus numérisés dans un compte de démonstration, montrent à quoi ça ressemble : [reçus Canadian Tire](/marchands/recus-canadian-tire), [reçus IKEA](/marchands/recus-ikea), [reçus Home Depot](/marchands/recus-home-depot) et [reçus RONA](/marchands/recus-rona).",
            "ReFound aide aussi pour les [retours en magasin](/application-retours-magasin), le [suivi des garanties](/application-garanties) et le [partage familial des reçus](/application-recus-famille).",
          ],
        },
        {
          heading: "Les 3 critères d'une bonne application de reçus",
          body: [
            "Peu importe l'application que vous choisissez, trois critères font la différence entre un gadget et un outil fiable : la précision de la lecture, la sécurité du stockage et la facilité à retrouver un reçu.",
          ],
          bullets: [
            "[Précision OCR](/fonctionnalites/precision-ocr) : ReFound lit vos reçus avec Google Vision, au sommet des benchmarks OCR indépendants, et Gemini pour les reçus difficiles à lire.",
            "[Stockage cloud sécurisé](/fonctionnalites/stockage-cloud-securise) : vos reçus restent privés et protégés, avec la possibilité d'activer la sauvegarde cloud — un téléphone perdu n'est plus un reçu perdu.",
            "[Catégorisation et filtres](/fonctionnalites/categorisation-filtres) : catégories automatiques et personnalisées, filtres par magasin, carte de paiement ou montant.",
          ],
          sub: [
            {
              heading: "Comment comparer les applications ?",
              body: [
                "Notre guide d'achat évalue les applications populaires au Canada selon ces trois critères, avec un tableau comparatif : [comment choisir une application de reçus](/blog/comment-choisir-application-recus).",
              ],
            },
          ],
        },
      ],
      comparison: {
        title: "ReFound vs les autres façons de garder vos reçus",
        intro:
          "Beaucoup de méthodes gardent une copie du reçu, mais ne vous aident pas à le retrouver ni à l'utiliser plus tard.",
        refoundLabel: "ReFound",
        othersLabel: "Photo, Drive, courriel, tiroir",
        rows: [
          { label: "Reçu lisible avant que le papier s'efface", refound: "Oui", others: "Le papier s'efface" },
          { label: "Informations enrichies du produit", refound: "Quand disponible", others: "Non" },
          { label: "Recherche rapide du bon reçu", refound: "Oui", others: "Recherche manuelle" },
          { label: "Aide aux retours et garanties", refound: "Oui", others: "Non" },
          { label: "Partage avec un conjoint ou la famille", refound: "Oui", others: "Difficile" },
        ],
      },
      faqTitle: "Questions fréquentes sur l'application de reçus",
      faq: [
        {
          q: "À quoi sert une application de reçus?",
          a: "Une application de reçus aide à scanner, organiser et retrouver vos reçus. ReFound va plus loin en transformant vos reçus en preuves d'achat intelligentes, utiles pour les retours, les garanties et le partage familial.",
        },
        {
          q: "Qu'est-ce qu'un reçu intelligent?",
          a: "Un reçu intelligent est un reçu numérisé puis organisé pour être facilement retrouvé et utilisé. Il devient une preuve d'achat claire plutôt qu'une simple photo.",
        },
        {
          q: "Qu'est-ce qu'un reçu enrichi?",
          a: "Un reçu enrichi est un reçu auquel ReFound ajoute des informations utiles quand elles sont disponibles : nom de produit plus clair, image, catégorie, garantie du manufacturier ou date limite de retour estimée.",
        },
        {
          q: "Est-ce que tous les reçus sont enrichis?",
          a: "Non. L'enrichissement dépend du marchand et des données disponibles. Même sans enrichissement complet, ReFound garde une copie lisible et organisée de votre reçu.",
        },
        {
          q: "Sur quels appareils ReFound est-il disponible?",
          a: "ReFound est disponible sur iPhone (App Store) et sur Android (Google Play). L'application est gratuite à télécharger.",
        },
      ],
      ctaTitle: "Transformez vos reçus en preuves d'achat intelligentes",
      ctaBody:
        "ReFound transforme vos reçus en preuves d'achat intelligentes, enrichies avec les informations utiles pour les retours, les garanties, le partage familial et la recherche.",
      relatedTitle: "À lire ensuite",
      related: [
        { label: "Comment numériser ses reçus avec une application mobile", href: FR_LINKS.blogScan, desc: "Le guide pour bien scanner et conserver vos reçus." },
        { label: "Reçu effacé : quoi faire?", href: FR_LINKS.blogFaded, desc: "Sauver une preuve d'achat avant que le papier disparaisse." },
        { label: "Quelle est la meilleure application de reçus au Canada?", href: FR_LINKS.blogBest, desc: "Comment choisir selon vos besoins." },
        { label: "Application de retours en magasin", href: FR_LINKS.returns, desc: "Utiliser le bon reçu au comptoir des retours." },
      ],
    },
    en: {
      metaTitle: "Smart Receipt App for Returns and Warranties | ReFound",
      metaDescription:
        "ReFound is the Canadian receipt app that turns paper receipts into smart proof of purchase: scan, enrich and find them easily for returns and warranties.",
      keywords:
        "receipt app, best receipt app, scan receipts, smart receipts, enriched receipts, organize receipts, proof of purchase, receipt scanner app, receipt app Canada",
      breadcrumb: "Receipt app",
      eyebrow: "Receipt app",
      h1: "A receipt app that turns your receipts into smart proof of purchase",
      intro: [
        "ReFound is a Canadian mobile app that turns paper receipts into smart proof of purchase. Instead of keeping just a photo, ReFound helps you scan, enrich, organize and find your receipts for returns, warranties and sharing with a partner or family member.",
        "Built for consumers and families, the app keeps your everyday receipts in one place, ready when you need them.",
      ],
      sections: [
        {
          heading: "More than a photo: an enriched receipt",
          body: [
            "Most people snap a photo of a receipt and forget it in their camera roll. The problem: paper receipts often show cryptic codes, and thermal paper fades over time.",
            "ReFound can enrich your receipts with useful information, when it is available, so you actually know what you bought.",
          ],
          bullets: [
            "Clearer product name",
            "Product image",
            "Link to the product or merchant page",
            "Category",
            "Manufacturer warranty",
            "Estimated return deadline",
            "Information extracted from the receipt",
            "Receipt barcode when detected",
          ],
        },
        {
          heading: "Scan and digitize a receipt in seconds",
          body: [
            "Snap a photo of any receipt, from any merchant. ReFound reads the receipt information and keeps a readable copy before thermal paper ink fades away.",
            "For a full walkthrough, see [how to scan receipts with a mobile app](/en/blog/comment-numeriser-recus-application-mobile).",
          ],
        },
        {
          heading: "Find the right receipt fast",
          body: [
            "When it is time for a return or a warranty claim, the hardest part is often finding the right receipt. ReFound organizes your receipts for fast search by merchant, product or date.",
          ],
          bullets: [
            "Fast search for the right receipt",
            "Organized by merchant and category",
            "Household receipts in one place",
          ],
        },
        {
          heading: "Built for everyday shopping in Canada",
          body: [
            "ReFound works with receipts from any store: every receipt is digitized, organized and ready to use for a return, with the barcode when there is one. For a list of popular merchants like Canadian Tire, RONA, Home Depot, Walmart, Costco, Best Buy, Sports Experts, Decathlon, BMR, Canac, Staples, Dollarama, Giant Tiger, Jean Coutu, Shoppers and Loblaws, items are also enriched with the real product name, a photo, a link and sometimes the warranty when available.",
            "Concrete examples, taken from receipts scanned into a demo account, show what that looks like: [Canadian Tire receipts](/en/merchants/canadian-tire-receipts), [IKEA receipts](/en/merchants/ikea-receipts), [Home Depot receipts](/en/merchants/home-depot-receipts) and [RONA receipts](/en/merchants/rona-receipts).",
            "ReFound also helps with [in-store returns](/en/returns-app), [warranty tracking](/en/warranty-tracker-app) and [family receipt sharing](/en/family-receipt-app).",
          ],
        },
        {
          heading: "The 3 criteria of a good receipt app",
          body: [
            "Whichever app you choose, three criteria separate a gimmick from a reliable tool: reading accuracy, storage security and how easily you can find a receipt again.",
          ],
          bullets: [
            "[OCR accuracy](/en/features/ocr-accuracy): ReFound reads your receipts with Google Vision, top-ranked in independent OCR benchmarks, plus Gemini for hard-to-read receipts.",
            "[Secure cloud storage](/en/features/secure-cloud-storage): your receipts stay private and protected, with the option to turn on cloud backup — a lost phone no longer means a lost receipt.",
            "[Categories and filters](/en/features/categories-filters): automatic and custom categories, filters by store, payment card or amount.",
          ],
          sub: [
            {
              heading: "How do the apps compare?",
              body: [
                "Our buying guide assesses the apps popular in Canada against these three criteria, with a comparison table: [how to choose a receipt app](/en/blog/how-to-choose-receipt-app).",
              ],
            },
          ],
        },
      ],
      comparison: {
        title: "ReFound vs other ways to keep your receipts",
        intro:
          "Many methods keep a copy of the receipt, but do not help you find it or use it later.",
        refoundLabel: "ReFound",
        othersLabel: "Photo, Drive, email, drawer",
        rows: [
          { label: "Readable receipt before paper fades", refound: "Yes", others: "Paper fades" },
          { label: "Enriched product information", refound: "When available", others: "No" },
          { label: "Fast search for the right receipt", refound: "Yes", others: "Manual search" },
          { label: "Help with returns and warranties", refound: "Yes", others: "No" },
          { label: "Share with a partner or family", refound: "Yes", others: "Hard" },
        ],
      },
      faqTitle: "Receipt app FAQ",
      faq: [
        {
          q: "What is a receipt app used for?",
          a: "A receipt app helps you scan, organize and find your receipts. ReFound goes further by turning your receipts into smart proof of purchase, useful for returns, warranties and family sharing.",
        },
        {
          q: "What is a smart receipt?",
          a: "A smart receipt is a digitized receipt organized so it is easy to find and use. It becomes clear proof of purchase rather than just a photo.",
        },
        {
          q: "What is an enriched receipt?",
          a: "An enriched receipt is a receipt where ReFound adds useful information when available: a clearer product name, image, category, manufacturer warranty or estimated return deadline.",
        },
        {
          q: "Are all receipts enriched?",
          a: "No. Enrichment depends on the merchant and available data. Even without full enrichment, ReFound keeps a readable, organized copy of your receipt.",
        },
        {
          q: "Which devices is ReFound available on?",
          a: "ReFound is available on iPhone (App Store) and on Android (Google Play). The app is free to download.",
        },
      ],
      ctaTitle: "Turn your receipts into smart proof of purchase",
      ctaBody:
        "ReFound turns receipts into smart proof of purchase, enriched with useful information for returns, warranties, family sharing and search.",
      relatedTitle: "Read next",
      related: [
        { label: "How to scan receipts with a mobile app", href: EN_LINKS.blogScan, desc: "The guide to scanning and keeping your receipts." },
        { label: "Faded receipt: what to do?", href: EN_LINKS.blogFaded, desc: "Save proof of purchase before paper disappears." },
        { label: "What is the best receipt app in Canada?", href: EN_LINKS.blogBest, desc: "How to choose based on your needs." },
        { label: "In-store returns app", href: EN_LINKS.returns, desc: "Use the right receipt at the return counter." },
      ],
    },
  },

  /* ============================ 2. RETURNS ============================ */
  {
    key: "returns",
    frPath: FR_LINKS.returns,
    enPath: EN_LINKS.returns,
    image: "/og-base.png",
    imageAltFr: "Reçu prêt pour un retour en magasin avec code-barres dans ReFound",
    imageAltEn: "Return-ready receipt with barcode in ReFound",
    fr: {
      metaTitle: "Application pour retours en magasin avec reçu numérique | ReFound",
      metaDescription:
        "ReFound aide à retrouver et présenter le bon reçu pour vos retours en magasin, avec le code-barres quand disponible. Selon la politique du marchand.",
      keywords:
        "retour en magasin, application de retours, reçu numérique, code-barres du reçu, preuve d'achat, retrouver un reçu, retour avec téléphone, ReFound",
      breadcrumb: "Retours en magasin",
      eyebrow: "Retours en magasin",
      h1: "Faciliter vos retours en magasin avec votre téléphone",
      intro: [
        "ReFound aide à retrouver et présenter le bon reçu au moment d'un retour, directement depuis votre téléphone. Quand le code-barres du reçu est détecté, l'application peut l'afficher pour faciliter l'expérience au comptoir.",
        "L'acceptation d'un retour dépend toujours de la politique du marchand. ReFound facilite la présentation des informations utiles, sans rien garantir à votre place.",
      ],
      sections: [
        {
          heading: "Retrouver le bon reçu au bon moment",
          body: [
            "Le plus frustrant lors d'un retour, c'est de ne pas retrouver le reçu. ReFound garde vos reçus organisés et permet une recherche rapide par marchand, produit ou date, pour présenter le bon reçu sans fouiller.",
          ],
        },
        {
          heading: "Afficher le code-barres du reçu quand disponible",
          body: [
            "Plusieurs marchands scannent le code-barres du reçu pour retrouver la transaction. Quand ce code-barres est détecté sur votre reçu, ReFound peut l'afficher à l'écran. La disponibilité dépend du reçu et du marchand.",
          ],
          bullets: [
            "Image du reçu prête à présenter",
            "Code-barres du reçu affiché quand détecté",
            "Informations utiles regroupées (marchand, date, articles)",
          ],
        },
        {
          heading: "Présenter une preuve d'achat claire",
          body: [
            "ReFound aide à présenter une preuve d'achat lisible, même si le papier original a commencé à s'effacer. C'est utile quand le marchand demande à voir le reçu pour un retour ou un échange.",
            "Pas à pas : [comment faire un retour en magasin avec son téléphone](/blog/comment-faire-retour-magasin-avec-telephone).",
          ],
        },
        {
          heading: "Pratique pour toute la famille",
          body: [
            "Votre conjoint est au magasin, mais la facture est sur votre téléphone? ReFound permet de [partager le reçu](/application-recus-famille) rapidement pour qu'il puisse présenter les informations utiles au magasin, selon la politique du marchand.",
          ],
        },
      ],
      comparison: {
        title: "Au comptoir des retours : ReFound vs le reçu papier",
        refoundLabel: "ReFound",
        othersLabel: "Reçu papier / photo",
        rows: [
          { label: "Reçu toujours sur soi", refound: "Sur le téléphone", others: "Si on l'a gardé" },
          { label: "Reçu lisible", refound: "Oui", others: "Peut s'effacer" },
          { label: "Code-barres du reçu", refound: "Affiché quand détecté", others: "Si encore lisible" },
          { label: "Recherche du bon reçu", refound: "Rapide", others: "Manuelle" },
          { label: "Partage avec un proche", refound: "Oui", others: "Non" },
        ],
      },
      faqTitle: "Questions fréquentes sur les retours en magasin",
      faq: [
        {
          q: "Est-ce que ReFound peut aider pour les retours en magasin?",
          a: "Oui. ReFound aide à retrouver le bon reçu, à présenter l'image du reçu et à afficher le code-barres quand il est détecté. L'acceptation du retour dépend toujours de la politique du marchand.",
        },
        {
          q: "Est-ce que ReFound peut afficher le code-barres d'un reçu?",
          a: "Quand un code-barres est détecté sur votre reçu, ReFound peut l'afficher à l'écran pour faciliter le passage au comptoir. La disponibilité dépend du reçu et du marchand.",
        },
        {
          q: "Est-ce qu'un retour est garanti avec ReFound?",
          a: "Non. ReFound facilite la présentation des informations utiles, mais l'acceptation d'un retour dépend de la politique du marchand et du produit concerné.",
        },
        {
          q: "Est-ce que tous les marchands acceptent les reçus numériques?",
          a: "Pas nécessairement. Certains marchands acceptent une preuve d'achat numérique, d'autres préfèrent le reçu original. ReFound aide à présenter les informations utiles selon le marchand.",
        },
        {
          q: "Puis-je faire un retour si mon reçu papier est effacé?",
          a: "ReFound garde une copie lisible de votre reçu si vous l'avez scanné à temps. Cela peut aider à présenter une preuve d'achat même si le papier original s'est effacé, selon la politique du marchand.",
        },
      ],
      ctaTitle: "Présentez le bon reçu au bon moment",
      ctaBody:
        "ReFound aide à retrouver et présenter le bon reçu, avec le code-barres quand disponible, pour faciliter vos retours en magasin selon la politique du marchand.",
      relatedTitle: "À lire ensuite",
      related: [
        { label: "Comment faire un retour en magasin avec son téléphone", href: FR_LINKS.blogReturn, desc: "Présenter le bon reçu, étape par étape." },
        { label: "Application de reçus", href: FR_LINKS.receipts, desc: "Scanner, enrichir et retrouver vos reçus." },
        { label: "Reçu effacé : quoi faire?", href: FR_LINKS.blogFaded, desc: "Garder une preuve lisible à temps." },
        { label: "Partage familial des reçus", href: FR_LINKS.family, desc: "Partager un reçu avec un conjoint." },
      ],
    },
    en: {
      metaTitle: "In-Store Returns App with Digital Receipts | ReFound",
      metaDescription:
        "ReFound helps you find and present the right receipt for in-store returns, with the receipt barcode when available. Easier returns, based on merchant policy.",
      keywords:
        "in-store returns, returns app, digital receipt, receipt barcode, proof of purchase, find a receipt, return with phone, ReFound",
      breadcrumb: "In-store returns",
      eyebrow: "In-store returns",
      h1: "Make in-store returns easier with your phone",
      intro: [
        "ReFound helps you find and present the right receipt when it is time for a return, straight from your phone. When the receipt barcode is detected, the app can display it to make the counter experience smoother.",
        "Whether a return is accepted always depends on the merchant's policy. ReFound makes it easier to present the useful information, without guaranteeing anything on your behalf.",
      ],
      sections: [
        {
          heading: "Find the right receipt at the right moment",
          body: [
            "The most frustrating part of a return is not finding the receipt. ReFound keeps your receipts organized and lets you search fast by merchant, product or date, so you present the right receipt without digging.",
          ],
        },
        {
          heading: "Show the receipt barcode when available",
          body: [
            "Many merchants scan the receipt barcode to find the transaction. When that barcode is detected on your receipt, ReFound can display it on screen. Availability depends on the receipt and the merchant.",
          ],
          bullets: [
            "Receipt image ready to present",
            "Receipt barcode shown when detected",
            "Useful information grouped (merchant, date, items)",
          ],
        },
        {
          heading: "Present clear proof of purchase",
          body: [
            "ReFound helps you present readable proof of purchase, even if the original paper has started to fade. This is useful when a merchant asks to see the receipt for a return or exchange.",
            "Step by step: [how to return items in store with your phone](/en/blog/how-to-return-items-in-store-with-your-phone).",
          ],
        },
        {
          heading: "Handy for the whole family",
          body: [
            "Your partner is at the store, but the receipt is on your phone? ReFound lets you [share the receipt](/en/family-receipt-app) quickly so they can present the useful information at the store, based on merchant policy.",
          ],
        },
      ],
      comparison: {
        title: "At the return counter: ReFound vs paper receipt",
        refoundLabel: "ReFound",
        othersLabel: "Paper receipt / photo",
        rows: [
          { label: "Receipt always with you", refound: "On your phone", others: "If you kept it" },
          { label: "Readable receipt", refound: "Yes", others: "Can fade" },
          { label: "Receipt barcode", refound: "Shown when detected", others: "If still readable" },
          { label: "Finding the right receipt", refound: "Fast", others: "Manual" },
          { label: "Sharing with a loved one", refound: "Yes", others: "No" },
        ],
      },
      faqTitle: "In-store returns FAQ",
      faq: [
        {
          q: "Can ReFound help with in-store returns?",
          a: "Yes. ReFound helps you find the right receipt, present the receipt image and show the barcode when detected. Whether the return is accepted always depends on the merchant's policy.",
        },
        {
          q: "Can ReFound display a receipt barcode?",
          a: "When a barcode is detected on your receipt, ReFound can display it on screen to make the counter step easier. Availability depends on the receipt and the merchant.",
        },
        {
          q: "Is a return guaranteed with ReFound?",
          a: "No. ReFound makes it easier to present the useful information, but whether a return is accepted depends on the merchant's policy and the product.",
        },
        {
          q: "Do all merchants accept digital receipts?",
          a: "Not necessarily. Some merchants accept digital proof of purchase, others prefer the original receipt. ReFound helps you present the useful information based on the merchant.",
        },
        {
          q: "Can I return an item if my paper receipt has faded?",
          a: "ReFound keeps a readable copy of your receipt if you scanned it in time. That can help you present proof of purchase even if the original paper has faded, based on merchant policy.",
        },
      ],
      ctaTitle: "Present the right receipt at the right moment",
      ctaBody:
        "ReFound helps you find and present the right receipt, with the barcode when available, to make in-store returns easier based on merchant policy.",
      relatedTitle: "Read next",
      related: [
        { label: "How to return items in store with your phone", href: EN_LINKS.blogReturn, desc: "Present the right receipt, step by step." },
        { label: "Receipt app", href: EN_LINKS.receipts, desc: "Scan, enrich and find your receipts." },
        { label: "Faded receipt: what to do?", href: EN_LINKS.blogFaded, desc: "Keep readable proof in time." },
        { label: "Family receipt sharing", href: EN_LINKS.family, desc: "Share a receipt with a partner." },
      ],
    },
  },

  /* ============================ 3. WARRANTIES ============================ */
  {
    key: "warranties",
    frPath: FR_LINKS.warranties,
    enPath: EN_LINKS.warranties,
    image: "/og-base.png",
    imageAltFr: "Suivi des garanties et preuves d'achat dans ReFound",
    imageAltEn: "Warranty tracking and proof of purchase in ReFound",
    fr: {
      metaTitle: "Application de garanties et preuves d'achat | ReFound",
      metaDescription:
        "ReFound garde vos reçus, garanties et détails importants au même endroit. Retrouvez votre preuve d'achat au moment d'une réclamation ou d'un problème.",
      keywords:
        "garantie de produit, suivi de garantie, application de garantie, preuve d'achat, conserver un reçu, réclamation garantie, ReFound",
      breadcrumb: "Garanties",
      eyebrow: "Garanties et preuves d'achat",
      h1: "Conserver vos reçus et vos garanties au même endroit",
      intro: [
        "Une garantie ne sert à rien si vous ne retrouvez pas la preuve d'achat. ReFound aide à garder vos reçus, vos garanties et les informations utiles au même endroit, prêts au moment d'une réclamation.",
        "Quand l'information est disponible, l'application peut associer à un reçu la garantie du manufacturier et une date limite de retour estimée.",
      ],
      sections: [
        {
          heading: "Garder la preuve d'achat pour la garantie",
          body: [
            "Pour une réclamation de garantie, le marchand ou le fabricant demande presque toujours la preuve d'achat. ReFound conserve une copie lisible de votre reçu, même si le papier original finit par s'effacer.",
            "Conseils détaillés : [comment conserver ses reçus pour les garanties](/blog/comment-conserver-recus-garanties).",
          ],
        },
        {
          heading: "Informations de garantie quand disponibles",
          body: [
            "ReFound peut associer à certains produits des informations de garantie du manufacturier, quand elles sont disponibles. Cela aide à savoir si un produit pourrait encore être couvert.",
            "Encore faut-il savoir de quel produit il s'agit : sur un [reçu RONA](/marchands/recus-rona) de notre compte de démonstration, la ligne imprimée « THERMOSTAT INT/SMRTSENSOR PREM » ne donne ni la marque ni le modèle, alors qu'une réclamation les exige.",
          ],
          bullets: [
            "Garantie du manufacturier associée quand disponible",
            "Date limite de retour estimée",
            "Reçu et détails du produit regroupés",
          ],
        },
        {
          heading: "Retrouver le bon reçu au moment d'un problème",
          body: [
            "Quand un appareil tombe en panne plusieurs mois après l'achat, retrouver le reçu est souvent le vrai défi. ReFound permet une recherche rapide pour retrouver la preuve d'achat liée au produit.",
          ],
        },
        {
          heading: "Pensé pour les achats du quotidien et du foyer",
          body: [
            "Électroménagers, outils, électronique, articles de sport : ReFound regroupe les preuves d'achat importantes de la maison. Vous pouvez aussi [partager un reçu](/application-recus-famille) avec un conjoint si c'est lui qui s'occupe de la réclamation.",
          ],
        },
      ],
      comparison: {
        title: "ReFound vs garder ses garanties à la main",
        refoundLabel: "ReFound",
        othersLabel: "Tiroir, courriel, mémoire",
        rows: [
          { label: "Reçu conservé lisible", refound: "Oui", others: "Le papier s'efface" },
          { label: "Info de garantie", refound: "Quand disponible", others: "À chercher soi-même" },
          { label: "Retrouver la preuve d'achat", refound: "Recherche rapide", others: "Souvent perdu" },
          { label: "Date limite de retour estimée", refound: "Quand disponible", others: "Non" },
          { label: "Partage avec un proche", refound: "Oui", others: "Non" },
        ],
      },
      faqTitle: "Questions fréquentes sur les garanties",
      faq: [
        {
          q: "Est-ce que ReFound peut aider avec les garanties?",
          a: "Oui. ReFound aide à conserver vos reçus et, quand l'information est disponible, à associer la garantie du manufacturier et une date limite de retour estimée. Vous retrouvez ainsi plus facilement votre preuve d'achat.",
        },
        {
          q: "Est-ce que tous les produits ont une garantie enrichie?",
          a: "Non. Les informations de garantie sont ajoutées seulement quand elles sont disponibles, selon le marchand et le produit. ReFound conserve tout de même votre reçu comme preuve d'achat.",
        },
        {
          q: "Pourquoi conserver un reçu pour une garantie?",
          a: "La plupart des réclamations de garantie exigent une preuve d'achat. Garder le reçu au bon endroit aide à le retrouver au moment d'une réclamation ou d'un problème.",
        },
        {
          q: "Est-ce que ReFound m'avertit avant la fin d'une garantie?",
          a: "ReFound aide à garder les informations utiles au même endroit. La présentation des dates dépend des données disponibles pour le produit et le marchand.",
        },
        {
          q: "Est-ce que ReFound remplace la garantie du fabricant?",
          a: "Non. ReFound aide à conserver et retrouver la preuve d'achat et les informations utiles. La garantie reste celle offerte par le marchand ou le fabricant.",
        },
      ],
      ctaTitle: "Gardez vos preuves d'achat prêtes pour la garantie",
      ctaBody:
        "ReFound aide à garder vos reçus, vos garanties et les détails importants au même endroit, prêts au moment d'une réclamation.",
      relatedTitle: "À lire ensuite",
      related: [
        { label: "Comment conserver ses reçus pour les garanties", href: FR_LINKS.blogWarranty, desc: "Garder la bonne preuve d'achat, au bon endroit." },
        { label: "Application de reçus", href: FR_LINKS.receipts, desc: "Scanner et enrichir vos reçus." },
        { label: "Application de retours en magasin", href: FR_LINKS.returns, desc: "Présenter le bon reçu au comptoir." },
        { label: "Reçu effacé : quoi faire?", href: FR_LINKS.blogFaded, desc: "Avant que le papier ne disparaisse." },
      ],
    },
    en: {
      metaTitle: "Warranty Tracker and Proof of Purchase App | ReFound",
      metaDescription:
        "ReFound helps you keep your receipts, warranties and important details in one place. Find your proof of purchase when it is time for a claim or a problem.",
      keywords:
        "product warranty, warranty tracker app, proof of purchase, keep receipts, warranty claim, digital proof of purchase, ReFound",
      breadcrumb: "Warranties",
      eyebrow: "Warranties and proof of purchase",
      h1: "Keep your receipts and warranties in one place",
      intro: [
        "A warranty is useless if you cannot find the proof of purchase. ReFound helps you keep your receipts, warranties and useful information in one place, ready when it is time for a claim.",
        "When the information is available, the app can attach the manufacturer warranty and an estimated return deadline to a receipt.",
      ],
      sections: [
        {
          heading: "Keep proof of purchase for the warranty",
          body: [
            "For a warranty claim, the merchant or manufacturer almost always asks for proof of purchase. ReFound keeps a readable copy of your receipt, even if the original paper eventually fades.",
            "Detailed tips: [how to keep receipts for warranties](/en/blog/how-to-keep-receipts-for-warranties).",
          ],
        },
        {
          heading: "Warranty information when available",
          body: [
            "ReFound can attach manufacturer warranty information to some products, when it is available. That helps you know whether a product might still be covered.",
            "You still need to know which product it was: on a [RONA receipt](/en/merchants/rona-receipts) from our demo account, the printed line \"THERMOSTAT INT/SMRTSENSOR PREM\" gives neither the brand nor the model, which a claim requires.",
          ],
          bullets: [
            "Manufacturer warranty attached when available",
            "Estimated return deadline",
            "Receipt and product details grouped together",
          ],
        },
        {
          heading: "Find the right receipt when a problem happens",
          body: [
            "When a device breaks months after purchase, finding the receipt is often the real challenge. ReFound lets you search fast to find the proof of purchase tied to the product.",
          ],
        },
        {
          heading: "Built for everyday and household purchases",
          body: [
            "Appliances, tools, electronics, sporting goods: ReFound groups your household's important proofs of purchase. You can also [share a receipt](/en/family-receipt-app) with a partner if they are the one handling the claim.",
          ],
        },
      ],
      comparison: {
        title: "ReFound vs keeping warranties by hand",
        refoundLabel: "ReFound",
        othersLabel: "Drawer, email, memory",
        rows: [
          { label: "Receipt kept readable", refound: "Yes", others: "Paper fades" },
          { label: "Warranty info", refound: "When available", others: "Look it up yourself" },
          { label: "Finding proof of purchase", refound: "Fast search", others: "Often lost" },
          { label: "Estimated return deadline", refound: "When available", others: "No" },
          { label: "Sharing with a loved one", refound: "Yes", others: "No" },
        ],
      },
      faqTitle: "Warranty FAQ",
      faq: [
        {
          q: "Can ReFound help track warranties?",
          a: "Yes. ReFound helps you keep your receipts and, when the information is available, attach the manufacturer warranty and an estimated return deadline. That makes your proof of purchase easier to find.",
        },
        {
          q: "Do all products get an enriched warranty?",
          a: "No. Warranty information is added only when available, based on the merchant and product. ReFound still keeps your receipt as proof of purchase.",
        },
        {
          q: "Why keep a receipt for a warranty?",
          a: "Most warranty claims require proof of purchase. Keeping the receipt in the right place helps you find it when it is time for a claim or a problem.",
        },
        {
          q: "Does ReFound warn me before a warranty ends?",
          a: "ReFound helps keep the useful information in one place. Showing dates depends on the data available for the product and merchant.",
        },
        {
          q: "Does ReFound replace the manufacturer warranty?",
          a: "No. ReFound helps you keep and find proof of purchase and useful information. The warranty itself is the one offered by the merchant or manufacturer.",
        },
      ],
      ctaTitle: "Keep your proof of purchase ready for warranty claims",
      ctaBody:
        "ReFound helps you keep your receipts, warranties and important details in one place, ready when it is time for a claim.",
      relatedTitle: "Read next",
      related: [
        { label: "How to keep receipts for warranties", href: EN_LINKS.blogWarranty, desc: "Keep the right proof of purchase, in the right place." },
        { label: "Receipt app", href: EN_LINKS.receipts, desc: "Scan and enrich your receipts." },
        { label: "In-store returns app", href: EN_LINKS.returns, desc: "Present the right receipt at the counter." },
        { label: "Faded receipt: what to do?", href: EN_LINKS.blogFaded, desc: "Before the paper disappears." },
      ],
    },
  },

  /* ============================ 4. FAMILY ============================ */
  {
    key: "family",
    frPath: FR_LINKS.family,
    enPath: EN_LINKS.family,
    image: "/og-base.png",
    imageAltFr: "Partage d'un reçu avec un conjoint dans ReFound",
    imageAltEn: "Sharing a receipt with a partner in ReFound",
    fr: {
      metaTitle: "Application de reçus pour familles au Canada | ReFound",
      metaDescription:
        "ReFound aide les familles à organiser et partager leurs reçus. Partagez un reçu avec un proche pour les retours, garanties et achats du quotidien.",
      keywords:
        "reçus de famille, application de reçus famille, partage de reçu, partager un reçu conjoint, reçus du foyer, preuve d'achat famille, ReFound",
      breadcrumb: "Reçus en famille",
      eyebrow: "Reçus pour les familles",
      h1: "Organiser et partager les reçus importants de la famille",
      intro: [
        "Les reçus importants ne devraient plus rester coincés dans le téléphone d'une seule personne. ReFound aide les familles à organiser leurs reçus et à les partager avec un conjoint ou un membre du foyer.",
        "Pensé pour les vrais achats du quotidien au Canada : retours, garanties et preuves d'achat de la maison.",
      ],
      sections: [
        {
          heading: "Partager un reçu avec un conjoint",
          body: [
            "Votre conjoint est au magasin, mais la facture est sur votre téléphone? ReFound vous aide à lui partager le reçu rapidement, pour qu'il puisse présenter les informations utiles au magasin, selon la politique du marchand.",
            "Le détail du cas d'usage : [partager un reçu avec son conjoint ou sa famille](/blog/partager-recu-conjoint-famille).",
          ],
        },
        {
          heading: "Pratique pour les retours et les garanties de la famille",
          body: [
            "Quand un membre de la famille doit faire un retour ou une réclamation de garantie, le bon reçu est souvent dans un autre compte ou un autre téléphone. Le partage évite ce blocage.",
          ],
          bullets: [
            "Partage avec un conjoint ou un membre de la famille",
            "Pratique pour les retours, les garanties et les achats familiaux",
            "Les reçus importants ne restent plus coincés dans un seul téléphone",
          ],
        },
        {
          heading: "Garder les achats du foyer organisés",
          body: [
            "Épicerie, quincaillerie, vêtements, électronique : ReFound regroupe les reçus du quotidien pour que la famille retrouve facilement une preuve d'achat. Chaque reçu peut être [enrichi](/application-recus) quand l'information est disponible.",
          ],
        },
        {
          heading: "Un partage maîtrisé",
          body: [
            "Le partage est pensé pour un conjoint ou un membre de la famille, pas pour une diffusion publique. Vous choisissez le reçu que vous partagez.",
          ],
        },
      ],
      comparison: {
        title: "ReFound vs s'envoyer des reçus par message",
        refoundLabel: "ReFound",
        othersLabel: "Texto, courriel, photo",
        rows: [
          { label: "Reçu lisible et organisé", refound: "Oui", others: "Photo perdue dans le fil" },
          { label: "Retrouver le reçu plus tard", refound: "Recherche rapide", others: "Difficile" },
          { label: "Informations enrichies", refound: "Quand disponible", others: "Non" },
          { label: "Partage avec un proche", refound: "Pensé pour ça", others: "À refaire chaque fois" },
        ],
      },
      faqTitle: "Questions fréquentes sur les reçus en famille",
      faq: [
        {
          q: "Est-ce que je peux partager un reçu avec mon conjoint ou ma famille?",
          a: "Oui. ReFound permet de partager un reçu avec un conjoint ou un membre de la famille, par exemple quand l'un est au magasin et que la facture est sur le téléphone de l'autre.",
        },
        {
          q: "À quoi sert le partage familial des reçus?",
          a: "Il évite que les reçus importants restent coincés dans le téléphone d'une seule personne. C'est pratique pour les retours, les garanties et les achats familiaux, selon la politique du marchand.",
        },
        {
          q: "Est-ce que je peux partager un reçu publiquement avec n'importe qui?",
          a: "Non. Le partage est pensé pour un conjoint ou un membre de la famille. Vous choisissez le reçu que vous partagez.",
        },
        {
          q: "Est-ce que toute la famille a besoin de l'application?",
          a: "ReFound est disponible sur iPhone et Android. Le partage aide à transmettre les informations utiles d'un reçu à un proche au bon moment.",
        },
        {
          q: "Est-ce que ReFound est fait pour les achats d'entreprise?",
          a: "ReFound est pensé pour les consommateurs et les familles : retours, garanties et preuves d'achat du quotidien, plutôt que la comptabilité ou les dépenses d'entreprise.",
        },
      ],
      ctaTitle: "Les reçus de la famille, au même endroit",
      ctaBody:
        "Avec ReFound, les reçus importants ne restent plus coincés dans le téléphone d'une seule personne. Partagez-les avec un conjoint pour les retours, les garanties et les achats du quotidien.",
      relatedTitle: "À lire ensuite",
      related: [
        { label: "Partager un reçu avec son conjoint ou sa famille", href: FR_LINKS.blogShare, desc: "Le bon réflexe quand l'un est au magasin." },
        { label: "Application de reçus", href: FR_LINKS.receipts, desc: "Scanner, enrichir et retrouver vos reçus." },
        { label: "Application de retours en magasin", href: FR_LINKS.returns, desc: "Présenter le bon reçu au comptoir." },
        { label: "Application de garanties", href: FR_LINKS.warranties, desc: "Conserver vos preuves d'achat." },
      ],
    },
    en: {
      metaTitle: "Family Receipt App for Canadians | ReFound",
      metaDescription:
        "ReFound helps families organize and share important receipts. Share one with a partner or household member for returns, warranties and everyday purchases.",
      keywords:
        "family receipt app, share receipt, share receipt with partner, household receipts, family proof of purchase, ReFound",
      breadcrumb: "Family receipts",
      eyebrow: "Receipts for families",
      h1: "Organize and share your family's important receipts",
      intro: [
        "Important receipts should no longer stay locked on one person's phone. ReFound helps families organize their receipts and share them with a partner or household member.",
        "Built for real everyday shopping in Canada: returns, warranties and household proof of purchase.",
      ],
      sections: [
        {
          heading: "Share a receipt with a partner",
          body: [
            "Your partner is at the store, but the receipt is on your phone? ReFound helps you share it quickly, so they can present the useful information at the store, based on merchant policy.",
            "The full use case: [share a receipt with your partner or family](/en/blog/share-receipt-with-partner-family).",
          ],
        },
        {
          heading: "Handy for family returns and warranties",
          body: [
            "When a family member needs to make a return or a warranty claim, the right receipt is often in another account or another phone. Sharing removes that roadblock.",
          ],
          bullets: [
            "Share with a partner or family member",
            "Useful for returns, warranties and family purchases",
            "Important receipts no longer stay locked on one phone",
          ],
        },
        {
          heading: "Keep household purchases organized",
          body: [
            "Groceries, hardware, clothing, electronics: ReFound groups everyday receipts so the family can easily find proof of purchase. Each receipt can be [enriched](/en/receipt-app) when the information is available.",
          ],
        },
        {
          heading: "Sharing you control",
          body: [
            "Sharing is meant for a partner or family member, not public broadcasting. You choose which receipt you share.",
          ],
        },
      ],
      comparison: {
        title: "ReFound vs texting receipts back and forth",
        refoundLabel: "ReFound",
        othersLabel: "Text, email, photo",
        rows: [
          { label: "Readable, organized receipt", refound: "Yes", others: "Photo lost in the thread" },
          { label: "Find the receipt later", refound: "Fast search", others: "Hard" },
          { label: "Enriched information", refound: "When available", others: "No" },
          { label: "Sharing with a loved one", refound: "Built for it", others: "Redo every time" },
        ],
      },
      faqTitle: "Family receipts FAQ",
      faq: [
        {
          q: "Can I share a receipt with my partner or family?",
          a: "Yes. ReFound lets you share a receipt with a partner or family member, for example when one is at the store and the receipt is on the other's phone.",
        },
        {
          q: "What is family receipt sharing used for?",
          a: "It keeps important receipts from staying locked on one person's phone. It is useful for returns, warranties and family purchases, based on merchant policy.",
        },
        {
          q: "Can I share a receipt publicly with anyone?",
          a: "No. Sharing is meant for a partner or family member. You choose which receipt you share.",
        },
        {
          q: "Does everyone in the family need the app?",
          a: "ReFound is available on iPhone and Android. Sharing helps pass the useful information from a receipt to a loved one at the right moment.",
        },
        {
          q: "Is ReFound made for business purchases?",
          a: "ReFound is built for consumers and families: everyday returns, warranties and proof of purchase, rather than accounting or business expenses.",
        },
      ],
      ctaTitle: "Your family's receipts, in one place",
      ctaBody:
        "With ReFound, important receipts no longer stay locked on one person's phone. Share them with a partner for returns, warranties and everyday purchases.",
      relatedTitle: "Read next",
      related: [
        { label: "Share a receipt with your partner or family", href: EN_LINKS.blogShare, desc: "The right move when one of you is at the store." },
        { label: "Receipt app", href: EN_LINKS.receipts, desc: "Scan, enrich and find your receipts." },
        { label: "In-store returns app", href: EN_LINKS.returns, desc: "Present the right receipt at the counter." },
        { label: "Warranty tracker app", href: EN_LINKS.warranties, desc: "Keep your proof of purchase." },
      ],
    },
  },
];

export function getPillar(key: string): PillarPage | undefined {
  return pillars.find((p) => p.key === key);
}
